/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { usersService } from '../services/usersService'
import { useAuth } from '../contexts/AuthContext'
import { shipmentsService } from '../services/shipmentsService'
import { getShipmentRequester, getShipmentRequesterName } from '../utils/shipmentHelpers'

const AssignmentModal = ({ shipment, onClose, onAssign }) => {
    const { user } = useAuth()
    const [assignmentType, setAssignmentType] = useState('')
    const [selectedUserId, setSelectedUserId] = useState('')
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [hasTruckerBeenReassigned, setHasTruckerBeenReassigned] = useState(false)
    const [hasDriverBeenReassigned, setHasDriverBeenReassigned] = useState(false)
    const [checkingReassignment, setCheckingReassignment] = useState(true)

    // Check if user is super admin
    const isSuperAdmin = user?.email === 'muhammad.asad@cargo360pk.com' || user?.id === 126

    // Check if there's been a previous reassignment by examining logs
    useEffect(() => {
        const checkReassignmentHistory = async () => {
            if (!shipment?.shipmentId) {
                setCheckingReassignment(false)
                return
            }

            try {
                const result = await shipmentsService.getShipmentLogs(shipment.shipmentId, 100)
                if (result.success && result.data) {
                    // Track reassignments separately for trucker and driver
                    let truckerReassignmentCount = 0
                    let driverReassignmentCount = 0
                    
                    result.data.forEach((log) => {
                        if (log.operation === 'update' && log.diff) {
                            const diff = log.diff
                            
                            // Check all keys in diff - field names might be formatted (e.g., "Trucker ID" or "truckerId")
                            Object.keys(diff).forEach((key) => {
                                const keyLower = key.toLowerCase()
                                const diffValue = diff[key]
                                
                                // Handle both object format { old, new } and array format [old, new]
                                let oldValue, newValue
                                if (diffValue && typeof diffValue === 'object') {
                                    if (Array.isArray(diffValue)) {
                                        oldValue = diffValue[0]
                                        newValue = diffValue[1]
                                    } else {
                                        oldValue = diffValue.old
                                        newValue = diffValue.new
                                    }
                                } else {
                                    return // Skip if format is unexpected
                                }
                                
                                // Check if truckerId was reassigned (from non-null to different non-null)
                                if (keyLower === 'trucker id' || keyLower === 'truckerid' || key === 'truckerId') {
                                    if (oldValue !== null && oldValue !== undefined && 
                                        newValue !== null && newValue !== undefined && 
                                        oldValue !== newValue) {
                                        truckerReassignmentCount++
                                    }
                                }
                                
                                // Check if driverId was reassigned (from non-null to different non-null)
                                if (keyLower === 'driver id' || keyLower === 'driverid' || key === 'driverId') {
                                    if (oldValue !== null && oldValue !== undefined && 
                                        newValue !== null && newValue !== undefined && 
                                        oldValue !== newValue) {
                                        driverReassignmentCount++
                                    }
                                }
                            })
                        }
                    })
                    // Set flags separately - each can be reassigned once
                    setHasTruckerBeenReassigned(truckerReassignmentCount >= 1)
                    setHasDriverBeenReassigned(driverReassignmentCount >= 1)
                    
                    // Debug logging
                    console.log('Reassignment check:', {
                        truckerReassignmentCount,
                        driverReassignmentCount,
                        hasTruckerBeenReassigned: truckerReassignmentCount >= 1,
                        hasDriverBeenReassigned: driverReassignmentCount >= 1
                    })
                }
            } catch (error) {
                console.error('Error checking reassignment history:', error)
                // On error, assume no reassignment to be safe
                setHasTruckerBeenReassigned(false)
                setHasDriverBeenReassigned(false)
            } finally {
                setCheckingReassignment(false)
            }
        }

        checkReassignmentHistory()
    }, [shipment?.shipmentId])

    useEffect(() => {
        if (assignmentType) {
            fetchUsers()
        }
    }, [assignmentType])

    const fetchUsers = async () => {
        setLoading(true)
        try {
            const result = await usersService.getAllUsers({ role: assignmentType })
            if (result.success) {
                // Filter for approved users only
                // const approvedUsers = result.data.filter(user => user.isApproved && user.role === assignmentType)
                const approvedUsers = result.data.filter(user => user.role === assignmentType)
                setUsers(approvedUsers)
            } else {
                toast.error('Failed to fetch users')
                setUsers([])
            }
        } catch (error) {
            toast.error('Error fetching users')
            setUsers([])
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async () => {
        if (!assignmentType || !selectedUserId) {
            toast.error('Please select assignment type and user')
            return
        }

        // Check if trying to reassign (only if assigning to a different user)
        const isReassigningBroker = assignmentType === 'trucker' && 
            shipment.shipmentData?.Trucker && 
            shipment.shipmentData.Trucker.id !== parseInt(selectedUserId)
        const isReassigningDriver = assignmentType === 'driver' && 
            shipment.shipmentData?.Driver && 
            shipment.shipmentData.Driver.id !== parseInt(selectedUserId)

        if (isReassigningBroker) {
            // Only super admin can reassign
            if (!isSuperAdmin) {
                toast.error('Only super admin can reassign brokers')
                return
            }

            // Check if trucker has already been reassigned once
            if (hasTruckerBeenReassigned) {
                toast.error('This broker has already been reassigned once. Further reassignments are not allowed.')
                return
            }
        }

        if (isReassigningDriver) {
            // Only super admin can reassign
            if (!isSuperAdmin) {
                toast.error('Only super admin can reassign drivers')
                return
            }

            // Check if driver has already been reassigned once
            if (hasDriverBeenReassigned) {
                toast.error('This driver has already been reassigned once. Further reassignments are not allowed.')
                return
            }
        }

        setSubmitting(true)
        try {
            await onAssign(shipment.orderId, assignmentType, parseInt(selectedUserId))
            onClose()
        } catch (error) {
            toast.error('Failed to assign shipment')
        } finally {
            setSubmitting(false)
        }
    }

    const handleAssignmentTypeChange = (type) => {
        // Check if trying to reassign
        const isReassigningBroker = type === 'trucker' && shipment.shipmentData?.Trucker
        const isReassigningDriver = type === 'driver' && shipment.shipmentData?.Driver

        if (isReassigningBroker) {
            // Only super admin can reassign
            if (!isSuperAdmin) {
                toast.error('Only super admin can reassign brokers')
                return
            }

            // Check if trucker has already been reassigned once
            if (hasTruckerBeenReassigned) {
                toast.error('This broker has already been reassigned once. Further reassignments are not allowed.')
                return
            }
        }

        if (isReassigningDriver) {
            // Only super admin can reassign
            if (!isSuperAdmin) {
                toast.error('Only super admin can reassign drivers')
                return
            }

            // Check if driver has already been reassigned once
            if (hasDriverBeenReassigned) {
                toast.error('This driver has already been reassigned once. Further reassignments are not allowed.')
                return
            }
        }

        setAssignmentType(type)
        setSelectedUserId('')
        setUsers([])
    }

    // Check if broker or driver is already assigned
    const isBrokerAssigned = shipment.shipmentData?.Trucker !== null && shipment.shipmentData?.Trucker !== undefined
    const isDriverAssigned = shipment.shipmentData?.Driver !== null && shipment.shipmentData?.Driver !== undefined

    // Determine if reassignment should be disabled (not super admin or already reassigned once)
    // Track trucker and driver reassignments separately
    const canReassignBroker = isSuperAdmin && !hasTruckerBeenReassigned
    const canReassignDriver = isSuperAdmin && !hasDriverBeenReassigned

    // Clear assignment type if broker or driver is selected but reassignment is not allowed
    useEffect(() => {
        if (assignmentType === 'trucker' && isBrokerAssigned && !canReassignBroker) {
            setAssignmentType('')
            setSelectedUserId('')
            setUsers([])
        }
        if (assignmentType === 'driver' && isDriverAssigned && !canReassignDriver) {
            setAssignmentType('')
            setSelectedUserId('')
            setUsers([])
        }
    }, [isBrokerAssigned, isDriverAssigned, assignmentType, canReassignBroker, canReassignDriver])

    if (!shipment) return null

    return (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
            <div className="bg-[#f8f9fa] rounded-lg w-full max-w-[560px] max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-[#e9ecef] px-6 py-4 flex items-center justify-between rounded-t-lg">
                    <h2 className="text-base font-semibold text-[#2c3e50]">Assign Shipment</h2>
                    <button onClick={onClose} className="text-[#7f8c8d] hover:text-[#2c3e50] transition-colors text-lg">×</button>
                </div>

                <div className="p-5 space-y-4">
                    {/* Shipment Info */}
                    {(() => {
                        const shipmentData = shipment.shipmentData || shipment
                        const requester = getShipmentRequester(shipmentData)
                        return (
                            <div className="bg-white border border-[#e9ecef] rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <p className="text-xs text-[#7f8c8d] font-medium uppercase tracking-wide mb-0.5">Shipment ID</p>
                                    <p className="text-[#2c3e50]">{shipment.orderId}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-[#7f8c8d] font-medium uppercase tracking-wide mb-0.5">Company</p>
                                    <p className="text-[#2c3e50]">{requester.company || '—'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-[#7f8c8d] font-medium uppercase tracking-wide mb-0.5">Customer</p>
                                    <p className="text-[#2c3e50]">{shipment.customer || getShipmentRequesterName(shipmentData)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-[#7f8c8d] font-medium uppercase tracking-wide mb-0.5">Status</p>
                                    <p className="text-[#2c3e50]">{shipment.status?.toUpperCase()}</p>
                                </div>
                            </div>
                        )
                    })()}

                    {/* Current Assignment */}
                    {shipment.shipmentData && (shipment.shipmentData.Trucker || shipment.shipmentData.Driver) && (
                        <div className="bg-white border border-[#e9ecef] rounded-lg p-4">
                            <h3 className="text-sm font-semibold text-[#2c3e50] pb-3 mb-3 border-b border-[#e9ecef]">Current Assignment</h3>
                            <div className="space-y-3">
                                {shipment.shipmentData.Trucker && (
                                    <div>
                                        <p className="text-xs text-[#7f8c8d] font-medium uppercase tracking-wide mb-1">Assigned Trucker / Broker</p>
                                        <p className="text-sm font-semibold text-[#2c3e50]">{shipment.shipmentData.Trucker.name}</p>
                                        <p className="text-xs text-[#7f8c8d]">ID: {shipment.shipmentData.Trucker.id} · {shipment.shipmentData.Trucker.email}</p>
                                        {shipment.shipmentData.Trucker.phone && <p className="text-xs text-[#7f8c8d]">Phone: {shipment.shipmentData.Trucker.phone}</p>}
                                    </div>
                                )}
                                {shipment.shipmentData.Driver && (
                                    <div>
                                        <p className="text-xs text-[#7f8c8d] font-medium uppercase tracking-wide mb-1">Assigned Driver</p>
                                        <p className="text-sm font-semibold text-[#2c3e50]">{shipment.shipmentData.Driver.name}</p>
                                        <p className="text-xs text-[#7f8c8d]">ID: {shipment.shipmentData.Driver.id} · {shipment.shipmentData.Driver.email}</p>
                                        {shipment.shipmentData.Driver.phone && <p className="text-xs text-[#7f8c8d]">Phone: {shipment.shipmentData.Driver.phone}</p>}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Assignment Type */}
                    <div className="bg-white border border-[#e9ecef] rounded-lg p-4">
                        <p className="text-xs text-[#7f8c8d] font-medium uppercase tracking-wide mb-3">Assignment Type</p>
                        <div className="flex gap-4">
                            <label className={`flex items-center gap-2 text-sm ${isBrokerAssigned && !canReassignBroker ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                                <input type="radio" name="assignmentType" value="trucker" checked={assignmentType === 'trucker'}
                                    onChange={(e) => handleAssignmentTypeChange(e.target.value)} disabled={isBrokerAssigned && !canReassignBroker}
                                    className="w-4 h-4 text-[#3498db]" />
                                <span className="text-[#2c3e50]">Broker
                                    {isBrokerAssigned && <span className="text-xs text-[#7f8c8d] ml-1">({hasTruckerBeenReassigned ? 'Already reassigned' : 'Already assigned'})</span>}
                                </span>
                            </label>
                            <label className={`flex items-center gap-2 text-sm ${isDriverAssigned && !canReassignDriver ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                                <input type="radio" name="assignmentType" value="driver" checked={assignmentType === 'driver'}
                                    onChange={(e) => handleAssignmentTypeChange(e.target.value)} disabled={isDriverAssigned && !canReassignDriver}
                                    className="w-4 h-4 text-[#3498db]" />
                                <span className="text-[#2c3e50]">Driver
                                    {isDriverAssigned && <span className="text-xs text-[#7f8c8d] ml-1">({hasDriverBeenReassigned ? 'Already reassigned' : 'Already assigned'})</span>}
                                </span>
                            </label>
                        </div>
                        {checkingReassignment && <p className="text-xs text-[#7f8c8d] mt-2">Checking reassignment history…</p>}
                        {!checkingReassignment && isBrokerAssigned && !canReassignBroker && (
                            <p className="text-xs text-[#7f8c8d] mt-2">
                                {hasTruckerBeenReassigned ? 'This broker has already been reassigned once.' : !isSuperAdmin ? 'A broker is already assigned. Only super admin can reassign.' : 'A broker is already assigned to this shipment.'}
                            </p>
                        )}
                        {!checkingReassignment && isDriverAssigned && !canReassignDriver && (
                            <p className="text-xs text-[#7f8c8d] mt-2">
                                {hasDriverBeenReassigned ? 'This driver has already been reassigned once.' : !isSuperAdmin ? 'A driver is already assigned. Only super admin can reassign.' : 'A driver is already assigned to this shipment.'}
                            </p>
                        )}
                        {!checkingReassignment && isBrokerAssigned && isSuperAdmin && !hasTruckerBeenReassigned && (
                            <p className="text-xs text-[#3498db] mt-2">As super admin, you can reassign the broker once.</p>
                        )}
                        {!checkingReassignment && isDriverAssigned && isSuperAdmin && !hasDriverBeenReassigned && (
                            <p className="text-xs text-[#3498db] mt-2">As super admin, you can reassign the driver once.</p>
                        )}
                    </div>

                    {/* User Selection */}
                    {assignmentType && (
                        <div className="bg-white border border-[#e9ecef] rounded-lg p-4">
                            <p className="text-xs text-[#7f8c8d] font-medium uppercase tracking-wide mb-2">
                                Select {assignmentType.charAt(0).toUpperCase() + assignmentType.slice(1)}
                            </p>
                            {loading ? (
                                <p className="text-sm text-[#7f8c8d]">Loading users…</p>
                            ) : (
                                <select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} disabled={users.length === 0}
                                    className="w-full border border-[#e9ecef] focus:border-[#3498db] rounded-lg px-3 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors">
                                    <option value="">{users.length === 0 ? `No approved ${assignmentType}s available` : `Select a ${assignmentType}`}</option>
                                    {users.map((user) => (
                                        <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-1">
                        <button onClick={onClose} disabled={submitting}
                            className="flex-1 text-sm text-[#7f8c8d] border border-[#e9ecef] py-2.5 px-4 rounded-lg hover:text-[#2c3e50] transition-colors">
                            Cancel
                        </button>
                        <button onClick={handleSubmit} disabled={!assignmentType || !selectedUserId || submitting}
                            className="flex-1 text-sm bg-[#3498db] hover:bg-[#2980b9] text-white py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold">
                            {submitting ? 'Assigning…' : 'Assign'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AssignmentModal
